import React, { useRef, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Household from "./pages/Household";
import Industrial from "./pages/Industrial";
import ProductsPage from "./pages/ProductsPage";
import TipsAndArticles from "./pages/Blog";
import About from "./pages/About";
import ProductDetail from "./pages/ProductDetail";
import LoadingScreen from "./components/LoadingScreen";
import { fetchProducts } from "./contentful";
import ScrollToTop from "./components/ScrollToTop";
import ScrollLock from "./components/ScrollLock";
import { Analytics } from "@vercel/analytics/react"
import { CONTENT_TYPES } from "./constants/contentTypes";
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import Navbar from "./components/Navbar";
import TestPage from "./pages/TestPage";
import InTheNews from "./pages/InTheNews";
import NewsArticle from "./pages/NewsArticle";
import CookiePolicy from "./pages/CookiePolicy";
import DoNotSellOrShare from "./pages/DoNotSellOrShare";
import CookieConsentManager from "./components/consent/CookieConsentManager";
import { analyticsOptedOut } from "./utils/consent";

const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <p className="mb-4 text-gray-700">{children}</p>,
    [BLOCKS.HEADING_1]: (node, children) => <h1 className="text-4xl font-bold mb-6 text-gray-900">{children}</h1>,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { url, description } = node.data.target.fields.file;
      return (
        <img
          src={url}
          alt={description || ''}
          className="w-full rounded-lg my-6"
        />
      );
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text) => <strong className="font-bold">{text}</strong>,
    [MARKS.ITALIC]: (text) => <em className="italic">{text}</em>,
    [MARKS.UNDERLINE]: (text) => <u className="underline">{text}</u>,
    [MARKS.CODE]: (text) => <code className="bg-gray-100 px-1 rounded">{text}</code>,
  },
};

const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [householdProducts, setHouseholdProducts] = useState([]);
  const [professionalProducts, setProfessionalProducts] = useState([]);
  const footerRef = useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleLoad = () => {
      setIsLoading(false);
    };

    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load household products
        const householdFetchedProducts = await fetchProducts(CONTENT_TYPES.HOUSEHOLD);
        setHouseholdProducts(householdFetchedProducts);

        // Load professional products
        const professionalFetchedProducts = await fetchProducts(CONTENT_TYPES.PROFESSIONAL);
        setProfessionalProducts(professionalFetchedProducts);
      } catch (error) {
        console.error("Error in loadProducts:", error);
      }
    };

    loadProducts();
  }, []);

  const getProductsByType = (type) => {
    return type === 'professional' ? professionalProducts : householdProducts;
  };

  if (isLoading) {
    return <LoadingScreen isLoading={isLoading} />;
  }

  return (
    <ScrollLock>
      {!isHomePage && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="relative z-40">
            <Navbar />
            <div className="shadow-[0_8px_16px_rgba(0,0,0,0.3)]">
              <Nav />
            </div>
          </div>
        </div>
      )}
      <div className={isHomePage ? "" : "pt-32"}>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/household" element={<Household footerRef={footerRef} />} />
          <Route path="/household/products" element={<ProductsPage />} />
          <Route path="/household/product/:type/:productTitle" element={<ProductDetail products={getProductsByType} />} />
          <Route path="/household/tips-and-articles" element={<TipsAndArticles />} />
          <Route path="/household/tips-and-articles/:blogId" element={<TipsAndArticles />} />
          <Route path="/household/about" element={<About />} />
          <Route path="/household/news" element={<InTheNews />} />
          <Route path="/household/news/:title" element={<NewsArticle />} />
          <Route path="/household/news/:newsId" element={<InTheNews />} />
          <Route path="/industrial" element={<Industrial footerRef={footerRef} />} />
          <Route path="/industrial/products" element={<ProductsPage />} />
          <Route path="/industrial/product/:type/:productTitle" element={<ProductDetail products={getProductsByType} />} />
          <Route path="/industrial/tips-and-articles" element={<TipsAndArticles />} />
          <Route path="/industrial/tips-and-articles/:blogId" element={<TipsAndArticles />} />
          <Route path="/industrial/about" element={<About />} />
          <Route path="/industrial/news" element={<InTheNews />} />
          <Route path="/industrial/news/:title" element={<NewsArticle />} />
          <Route path="/industrial/news/:newsId" element={<InTheNews />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/do-not-sell-or-share" element={<DoNotSellOrShare />} />
          <Route path="/test" element={<TestPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {!isHomePage && (
          <div ref={footerRef}>
            <Footer />
          </div>
        )}
      </div>
      {/* Vercel Analytics is cookieless; an explicit analytics opt-out drops every event. */}
      <Analytics beforeSend={(event) => (analyticsOptedOut() ? null : event)} />
      <CookieConsentManager />
    </ScrollLock>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;